import { Settings } from '@streamr/tv-engine/~/settings';
import { proxyIfNeeded } from '../helpers/comlinkHelpers';
import { Track } from './Track';
export class AudioEncoder {
    LOG_PREFIX = '[AudioEncoder]';
    audioEncoder;
    frameFifo = [];
    done = false;
    inputTrack;
    encodedOutputTrack;
    encodedFrameCount = 0;
    trackId;
    constructor(trackId) {
        this.trackId = trackId;
        this.encodedOutputTrack = new Track(trackId, true);
        console.log(`${this.LOG_PREFIX} constructor called`, { trackId });
    }
    async start(inputTrack) {
        console.log(`${this.LOG_PREFIX} start called`);
        this.inputTrack = inputTrack;
        // Create and configure the AudioEncoder
        this.audioEncoder = new globalThis.AudioEncoder({
            output: this.onEncodedChunk,
            error: (error) => {
                console.error(`${this.LOG_PREFIX} encoder error:`, error);
            },
        });
        const config = {
            codec: Settings.audioCodec, // 'opus'
            sampleRate: Settings.audioSampleRate, // 48000
            numberOfChannels: Settings.audioNumberOfChannels, // 1
            bitrate: 64000, // 64 kbps for voice
        };
        // Check if config is supported
        const support = await globalThis.AudioEncoder.isConfigSupported(config);
        if (!support.supported) {
            throw new Error(`${this.LOG_PREFIX} Audio encoder config not supported: ${JSON.stringify(config)}`);
        }
        this.audioEncoder.configure(config);
        console.log(`${this.LOG_PREFIX} encoder configured`, { config });
        this.inputTrack.setListener(proxyIfNeeded(this.inputTrack, this.onUnencodedAudioFrameFromInputTrack));
        console.log(`${this.LOG_PREFIX} start completed`);
    }
    onUnencodedAudioFrameFromInputTrack = async (unencodedAudioFrame, done) => {
        if (done) {
            console.log(`${this.LOG_PREFIX} done received from input track`);
        }
        await this.feedUnencodedAudioFrame(unencodedAudioFrame, done).catch(console.error);
    };
    onEncodedChunk = (chunk, _meta) => {
        const unencodedAudioFrame = this.popFrameFromFifo();
        if (!unencodedAudioFrame) {
            console.error(`${this.LOG_PREFIX} No unencoded audio frame found in fifo`);
            return;
        }
        const encodedAudioFrame = this.createEncodedAudioFrame(chunk, unencodedAudioFrame);
        this.encodedFrameCount++;
        /*
        if (this.encodedFrameCount <= 3 || this.encodedFrameCount % 100 === 0) {
            console.log(`${this.LOG_PREFIX} encoded frame`, {
                frameNumber: this.encodedFrameCount,
                dataSize: encodedAudioFrame.data.length,
            });
        }
        */
        this.encodedOutputTrack?.emit(encodedAudioFrame, false, [
            encodedAudioFrame.data.buffer,
        ]);
        if (this.done && this.frameFifo.length === 0) {
            this.shutdown().catch(console.error);
        }
    };
    async feedUnencodedAudioFrame(unencodedAudioFrame, done) {
        if (this.done) {
            throw new Error(`${this.LOG_PREFIX} is done, cannot feed more frames`);
        }
        if (unencodedAudioFrame && this.audioEncoder) {
            this.pushFrameToFifo(unencodedAudioFrame);
            // Convert Float32Array to AudioData
            // Need to use the underlying ArrayBuffer for AudioData constructor
            const audioData = new AudioData({
                format: 'f32-planar', // Float32 planar format
                sampleRate: Settings.audioSampleRate,
                numberOfFrames: unencodedAudioFrame.data.length,
                numberOfChannels: Settings.audioNumberOfChannels,
                timestamp: Number(unencodedAudioFrame.timestamp),
                data: unencodedAudioFrame.data.buffer,
            });
            this.audioEncoder.encode(audioData);
            audioData.close();
        }
        if (done) {
            if (this.frameFifo.length === 0) {
                await this.shutdown();
            }
            else {
                this.done = true;
            }
        }
    }
    async shutdown() {
        console.log(`${this.LOG_PREFIX} shutdown called`, {
            totalEncodedFrames: this.encodedFrameCount,
        });
        this.inputTrack?.unsetListener();
        if (this.audioEncoder && this.audioEncoder.state !== 'closed') {
            await this.audioEncoder.flush();
            this.audioEncoder.close();
        }
        this.encodedOutputTrack?.emit(undefined, true);
        console.log(`${this.LOG_PREFIX} shutdown completed`);
    }
    pushFrameToFifo(frame) {
        this.frameFifo.push(frame);
    }
    popFrameFromFifo() {
        return this.frameFifo.shift();
    }
    createEncodedAudioFrame(chunk, unencodedAudioFrame) {
        const data = new Uint8Array(chunk.byteLength);
        chunk.copyTo(data);
        return {
            frameNumber: unencodedAudioFrame.frameNumber,
            timestamp: unencodedAudioFrame.timestamp,
            duration: unencodedAudioFrame.duration,
            trackId: unencodedAudioFrame.trackId,
            data: data,
            done: false,
            isKeyFrame: true, // All Opus frames are effectively keyframes
        };
    }
    getEncodedOutputTrack() {
        return this.encodedOutputTrack;
    }
}
